import { Form, Formik } from 'formik';
import { useState, useMemo, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import mixpanel from 'mixpanel-browser';

// ASSETS
import EditIcon from 'assets/EditIcon';

// COMPONENTS
import { AddButton, LightDangerButton, PrimaryButton, SVGButton } from 'components/Buttons';
import FlashMessage from 'components/FlashMessage';
import Field, { MultiCheckboxField, MultiSelectField, SelectField } from 'components/FormField';
import { useModal } from 'components/Modals';
import GeofenceMap from 'components/GeofenceMap';
import Slider from './geofencing.Slider.component';
import { RemoveModal } from 'components/Modals/Remove';

// CONSTANTS
import { INITIAL_FLASH_MESSAGE } from 'constants/common';
import { GeofenceActionType, GeofenceInteractionAction, GeofenceType } from 'constants/geofence';
import { ANALYTICS_EVENTS } from 'constants/mixpanel.constants';

// HOOKS
import { useAppDispatch } from 'hooks/store';

// SERVICES
import {
  addGeofence,
  addGeofenceRule,
  deleteGeofenceRule,
  updateGeofence,
  updateGeofenceRule,
} from 'services/Geofences/thunks';
import { getUsers } from 'services/Users/thunks';
import { selectUsers } from 'services/Users/selectors';

// STYLES
import { BUTTON_INTENT } from 'styles/buttons';
import colors from 'styles/colors';
import { Modal as ModalDialog, ModalFooter, ModalHeader, Title } from 'styles/modals';
import { StyledBackButton, StyledCancelButton } from 'components/Modals/styles';
import { GeofencingModalContent, MapContainer, MapContent, TypeContainer, TypeWrapper } from '../styles';

// TYPES
import { AuthUser, GeofenceRuleCreation, GeofenceRule } from 'services/resources';

type Values = Pick<GeofenceRule, 'actions' | 'geofence' | 'name' | 'id' | 'onEntry' | 'onExit'>;
type ValuesWithFormExtra = Values & {
  interactions: GeofenceInteractionAction[];
  actionsData: GeofenceActionType[];
  alertRecipients?: string[];
};

const initialValues: Values = {
  id: undefined,
  name: '',
  geofence: undefined,
  actions: [],
  onEntry: false,
  onExit: false,
};

const makeGeofenceInteractionFrom = (values: Values): GeofenceInteractionAction[] => {
  const interactions: GeofenceInteractionAction[] = [];
  Object.entries(values)
    // @ts-expect-error: typescript cannot infer proper type from Object.entries(values)
    .filter(([key]) => Object.values(GeofenceInteractionAction).includes(key))
    .forEach(([key, enabled]) => {
      if (enabled) {
        const action = Object.entries(GeofenceInteractionAction).find(([k, v]) => v === key);
        interactions.push(GeofenceInteractionAction[action[0]]);
      }
    });
  return interactions;
};

const makeActionsFrom = (values: Values): GeofenceActionType[] => {
  const actions: GeofenceActionType[] = [];
  values.actions.forEach((action) => {
    actions.push(action.type);
  });
  return actions;
};

const useGeofenceForm = (closeModal: (event?: React.SyntheticEvent) => void, values = initialValues, edit = false) => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const [ovalToolSelected, setOvalToolSelected] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const geofenceTypeOptions = useMemo(() => {
    const geofenceTypes = Object.values(GeofenceType);

    const options = geofenceTypes.map((type) => {
      return {
        label: formatMessage({
          id: `views.FleetSettings.Geofencing.types.${type}`,
        }),
        value: type,
      };
    });
    return options;
  }, [formatMessage]);

  // Building two additional form values, which are used by the inputs themselves (through names).
  // When the input values change, we trigger an update of their respective values (onEntry, onExit, actions)
  // See components implementations below (onChange/handleChange) for more information.
  const interactions = makeGeofenceInteractionFrom(values);
  const actionsData = makeActionsFrom(values);
  const recipients =
    values.actions
      .filter((action) => action.type === GeofenceActionType.ALERT)[0]
      ?.payload?.userIds.map((userId) => userId.toString()) ?? [];

  const formValues: ValuesWithFormExtra = { ...values, interactions, actionsData, alertRecipients: recipients };
  const schema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .nullable()
          .max(100)
          .required(formatMessage({ id: 'validators.yup.mixed.required' }, { path: 'Name' })),
        onEntry: Yup.boolean().required(formatMessage({ id: 'validators.yup.mixed.required' }, { path: 'On Entry' })),
        onExit: Yup.boolean().required(formatMessage({ id: 'validators.yup.mixed.required' }, { path: 'On Exit' })),
        actions: Yup.array().of(Yup.object()),
        geofence: Yup.object().required(formatMessage({ id: 'validators.yup.mixed.required' }, { path: 'Geofence' })),
      }),
    [formatMessage]
  );

  const onSubmit = async (newValues: ValuesWithFormExtra) => {
    setSubmitLoading(true);
    if (edit) {
      const requests = [
        // Update geofence
        dispatch(updateGeofence(values.geofence.id, newValues.name, newValues.geofence)),
        // Update geofenceRule
        dispatch(
          updateGeofenceRule(newValues.id, {
            name: newValues.name,
            onEntry: newValues.onEntry,
            onExit: newValues.onExit,
            actions: newValues.actions,
          })
        ),
      ];

      await Promise.all(requests);

      mixpanel.track(ANALYTICS_EVENTS.GEOFENCE_UPDATED);
      if (formValues.onEntry !== newValues.onEntry) {
        if (newValues.onEntry) {
          mixpanel.track(ANALYTICS_EVENTS.GEOFENCE_ALERT_ON_ENTRY_ENABLED);
        } else {
          mixpanel.track(ANALYTICS_EVENTS.GEOFENCE_ALERT_ON_ENTRY_DISABLED);
        }
      }
      if (formValues.onExit !== newValues.onExit) {
        if (newValues.onExit) {
          mixpanel.track(ANALYTICS_EVENTS.GEOFENCE_ALERT_ON_EXIT_ENABLED);
        } else {
          mixpanel.track(ANALYTICS_EVENTS.GEOFENCE_ALERT_ON_EXIT_DISABLED);
        }
      }
    } else {
      const geofenceId = await dispatch(addGeofence(newValues.name, newValues.geofence));

      const payload: GeofenceRuleCreation = {
        name: newValues.name,
        geofence: {
          id: geofenceId,
          geofenceType: GeofenceType.USER_DEFINED,
        },
        onExit: newValues.onExit,
        onEntry: newValues.onEntry,
        // @ts-expect-error: Incorrect mapping between Camelized<GeofenceAction>[] and GeofenceAction[]
        actions: newValues.actions,
      };

      await dispatch(addGeofenceRule(payload));

      mixpanel.track(ANALYTICS_EVENTS.GEOFENCE_CREATED);
      if (newValues.onEntry) {
        mixpanel.track(ANALYTICS_EVENTS.GEOFENCE_ALERT_ON_ENTRY_ENABLED);
      }
      if (newValues.onExit) {
        mixpanel.track(ANALYTICS_EVENTS.GEOFENCE_ALERT_ON_EXIT_ENABLED);
      }
    }
    setSubmitLoading(false);
    closeModal();
  };

  const handleSelectOvalTool = () => {
    if (ovalToolSelected) {
      return;
    }
    setOvalToolSelected(true);
  };

  const handleSelectPolygonTool = () => {
    if (!ovalToolSelected) {
      return;
    }
    setOvalToolSelected(false);
  };

  const onDelete = async () => {
    await setDeleteLoading(true);
  };

  return {
    geofenceTypeOptions,
    deleteLoading,
    initialValues: formValues,
    onDelete,
    onSubmit,
    schema,
    submitLoading,
    ovalToolSelected,
    handleSelectOvalTool,
    handleSelectPolygonTool,
    setCurrentStep,
    currentStep,
  };
};

interface NewGeofenceFormProps {
  closeModal: (event?: React.SyntheticEvent) => void;
  setFlashMessage: (flashMessage: typeof INITIAL_FLASH_MESSAGE) => void;
  edit?: false;
}

interface ExistingGeofenceFormProps {
  closeModal: (event?: React.SyntheticEvent) => void;
  values: GeofenceRule;
  setFlashMessage: (flashMessage: typeof INITIAL_FLASH_MESSAGE) => void;
  edit: true;
}

type GeofenceFormProps = NewGeofenceFormProps | ExistingGeofenceFormProps;

export const GeofenceForm = ({ closeModal, ...props }: GeofenceFormProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const users: AuthUser[] = useSelector(selectUsers());
  const {
    geofenceTypeOptions,
    initialValues,
    onSubmit,
    schema,
    submitLoading,
    ovalToolSelected,
    handleSelectOvalTool,
    handleSelectPolygonTool,
    setCurrentStep,
    currentStep,
  } = useGeofenceForm(closeModal, props.edit ? props.values : undefined, props.edit);

  const {
    openModal,
    closeModal: closeRemoveModal,
    isOpen,
    Modal,
  } = useModal({
    background: colors.modalBackground,
  });

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  return (
    <Formik initialValues={initialValues} validationSchema={schema} onSubmit={onSubmit}>
      {(formik) => {
        const { values } = formik;
        return (
          <>
            <ModalHeader>
              <Title>
                {props.edit ? (
                  <FormattedMessage id="views.FleetSettings.Geofencing.actions.editGeofence" />
                ) : (
                  <FormattedMessage id="views.FleetSettings.Geofencing.actions.addGeofence" />
                )}
              </Title>
              <LightDangerButton onClick={openModal}>
                <FormattedMessage id="buttons.remove" />
              </LightDangerButton>
            </ModalHeader>
            <Form>
              {currentStep === 1 && (
                <>
                  <GeofencingModalContent>
                    <Field name="name" label={formatMessage({ id: 'views.FleetSettings.Geofencing.name' })} />
                    <Field
                      as={SelectField}
                      name="type"
                      label={formatMessage({ id: 'views.FleetSettings.Geofencing.geofenceType' })}
                      options={geofenceTypeOptions}
                      value={GeofenceType.USER_DEFINED}
                      disabled
                    />
                    {(!props.edit ||
                      (props.edit && props.values.geofence.geofenceType === GeofenceType.USER_DEFINED)) && (
                      <div>
                        <MapContainer>
                          <MapContent>
                            <GeofenceMap
                              // @ts-expect-error issue with geofence guarding
                              displayGeofence={values.geofence}
                              // @ts-expect-error issue with geofence guarding
                              editGeofence={values.geofence}
                              ovalTool={ovalToolSelected}
                              polygonTool={!ovalToolSelected}
                              onSetCreatedGeofence={(geofence) => {
                                formik.setFieldValue('geofence', geofence);
                              }}
                              resizing={false}
                              editEnabled
                            />
                          </MapContent>
                        </MapContainer>
                        <TypeWrapper>
                          <TypeContainer>
                            <Slider
                              ovalToolSelected={ovalToolSelected}
                              handleSelectOvalTool={handleSelectOvalTool}
                              handleSelectPolygonTool={handleSelectPolygonTool}
                            />
                          </TypeContainer>
                        </TypeWrapper>
                      </div>
                    )}
                  </GeofencingModalContent>
                  <ModalFooter>
                    <StyledCancelButton onClick={closeModal} />
                    <PrimaryButton
                      intent={BUTTON_INTENT.primary}
                      onClick={() => {
                        setCurrentStep(2);
                      }}
                      loading={submitLoading}
                      disabled={(!props.edit && !formik.dirty) || !values.name || !values.geofence}
                    >
                      <FormattedMessage id="views.FleetSettings.Geofencing.actions.next" />
                    </PrimaryButton>
                  </ModalFooter>
                </>
              )}
              {currentStep === 2 && (
                <>
                  <GeofencingModalContent>
                    <Field
                      as={MultiSelectField}
                      name="interactions" // `interactions` is a mapping to the underlying boolean input.
                      label={formatMessage({ id: 'views.FleetSettings.Geofencing.actions.interactionActions' })}
                      onChange={(e) => {
                        const currentValues = { ...formik.values, interactions: e.target.value };
                        currentValues[GeofenceInteractionAction.ON_ENTRY] = e.target.value.includes(
                          GeofenceInteractionAction.ON_ENTRY
                        );
                        currentValues[GeofenceInteractionAction.ON_EXIT] = e.target.value.includes(
                          GeofenceInteractionAction.ON_EXIT
                        );

                        formik.setValues(currentValues);
                      }}
                      options={[
                        {
                          value: GeofenceInteractionAction.ON_ENTRY,
                          label: formatMessage({ id: 'views.FleetSettings.Geofencing.actions.vehicleEnters' }),
                        },
                        {
                          value: GeofenceInteractionAction.ON_EXIT,
                          label: formatMessage({ id: 'views.FleetSettings.Geofencing.actions.vehicleExits' }),
                        },
                      ]}
                    />
                    <Field
                      as={MultiCheckboxField}
                      label={formatMessage({ id: 'views.FleetSettings.Geofencing.actions.ruleActions' })}
                      name="actionsData"
                      handleChange={(value: GeofenceActionType[]) => {
                        // Get existing actions to preserve payloads
                        const existingActions = [...formik.values.actions];

                        // Create new actions array with proper types and preserved payloads
                        const newActions = value.map((actionType) => {
                          // Find if this action type already exists to preserve its payload
                          const existingAction = existingActions.find((action) => action.type === actionType);
                          return {
                            type: actionType,
                            payload: existingAction?.payload || {},
                          };
                        });

                        // Update the form values
                        formik.setFieldValue('actions', newActions);

                        // If ALERT is not in the selected actions, clear alertRecipients
                        if (!value.includes(GeofenceActionType.ALERT)) {
                          formik.setFieldValue('alertRecipients', []);
                        }
                      }}
                      options={[
                        {
                          value: GeofenceActionType.VIOLATION,
                          label: formatMessage({ id: 'views.FleetSettings.Geofencing.actions.addToAgreement' }),
                        },
                        {
                          value: GeofenceActionType.ALERT,
                          label: formatMessage({ id: 'views.FleetSettings.Geofencing.actions.alertUser' }),
                        },
                      ]}
                    />
                    {values.actions?.some((action) => action.type === GeofenceActionType.ALERT) && (
                      <Field
                        as={MultiSelectField}
                        name="alertRecipients"
                        label={formatMessage({ id: 'views.FleetSettings.Geofencing.actions.recipients' })}
                        options={users.map((user) => ({
                          value: user.id,
                          label: `${user.firstName} ${user.lastName}`,
                        }))}
                        onChange={(e) => {
                          const recipients = e.target.value;

                          // Set the field value
                          formik.setFieldValue('alertRecipients', recipients);

                          // Update the ALERT action payload with recipients
                          const updatedActions = formik.values.actions.map((action) => {
                            if (action.type === GeofenceActionType.ALERT) {
                              if (recipients.length > 0) {
                                return {
                                  ...action,
                                  payload: { ...action.payload, userIds: recipients },
                                };
                              } else {
                                return {
                                  ...action,
                                  payload: {},
                                };
                              }
                            }
                            return action;
                          });
                          // Set the updated actions back to the form
                          formik.setFieldValue('actions', updatedActions);
                        }}
                        value={values.actions
                          .find((action) => action.type === GeofenceActionType.ALERT)
                          ?.payload?.userIds?.toString()}
                      />
                    )}
                  </GeofencingModalContent>
                  <ModalFooter>
                    <StyledBackButton
                      onClick={() => {
                        setCurrentStep(1);
                      }}
                    />
                    <PrimaryButton
                      intent={BUTTON_INTENT.primary}
                      type="submit"
                      loading={submitLoading}
                      disabled={
                        !formik.dirty ||
                        !formik.isValid ||
                        !(formik.values.onEntry || formik.values.onExit) ||
                        (formik.values.alertRecipients?.length === 0 &&
                          formik.values.actions?.some((action) => {
                            return action.type === GeofenceActionType.ALERT;
                          }))
                      }
                    >
                      <FormattedMessage id="views.FleetSettings.Geofencing.actions.confirm" />
                    </PrimaryButton>
                  </ModalFooter>
                </>
              )}
            </Form>
            {isOpen && (
              <Modal>
                <RemoveModal
                  removedItemTitle={formatMessage({
                    id: 'views.FleetSettings.Geofence.deleteConfirmationTitle',
                  })}
                  removedItemDescription={[
                    formatMessage(
                      {
                        id: 'views.FleetSettings.Geofence.deleteConfirmationMessage',
                      },
                      {
                        name: initialValues.name,
                      }
                    ),
                  ]}
                  cancelCallback={() => closeRemoveModal()}
                  confirmCallback={async () => {
                    await dispatch(deleteGeofenceRule(initialValues.id));
                  }}
                />
              </Modal>
            )}
          </>
        );
      }}
    </Formik>
  );
};

interface NewGeofenceButtonProps extends React.ComponentPropsWithoutRef<typeof PrimaryButton> {
  edit?: false;
}

interface ExistingGeofenceButtonProps extends React.ComponentPropsWithoutRef<typeof PrimaryButton> {
  values: GeofenceRule;
  edit: true;
}

type GeofenceButtonProps = NewGeofenceButtonProps | ExistingGeofenceButtonProps;

const GeofenceButton = (props: GeofenceButtonProps) => {
  const { formatMessage } = useIntl();
  const [flashMessageState, setFlashMessageState] = useState(INITIAL_FLASH_MESSAGE);

  const { openModal, isOpen, Modal, closeModal } = useModal({
    background: colors.modalBackground,
    closeOnOutsideClick: false,
    closeOnEsc: false,
  });

  const onClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    mixpanel.track(props.edit ? 'Edit Geofence Modal Opened' : 'New Geofence Modal Opened');
    openModal(event);
  };

  return (
    <>
      {!props.edit ? (
        <AddButton onClick={onClick}>
          <FormattedMessage id="views.FleetSettings.Geofencing.actions.addGeofence" />
        </AddButton>
      ) : (
        <SVGButton color={colors.fleetBlue} onClick={onClick}>
          <EditIcon />
        </SVGButton>
      )}

      {flashMessageState.shown && (
        <FlashMessage
          type={flashMessageState.type}
          text={formatMessage({ id: flashMessageState.translationId }, flashMessageState.translationValueObject)}
          onClose={() => {
            setFlashMessageState(INITIAL_FLASH_MESSAGE);
          }}
          visible
        />
      )}
      {isOpen && (
        <Modal>
          <ModalDialog>
            <GeofenceForm
              edit={props.edit}
              values={props.edit ? props.values : undefined}
              setFlashMessage={setFlashMessageState}
              closeModal={closeModal}
            />
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};

export default { GeofenceButton };
